'use client';

import React from 'react';
import { RichPopover } from '@sharpr/ui/rich-popover';
import { cn } from '@sharpr/ui';
import type { LineupInfo, Sport } from '@sharpr/types';
import { LineupDrawerHeader, LineupTrigger } from './LineupTrigger';
import { NotesContent, NotesIcon } from './NotesContent';

// A player's notes trigger and popover (a drawer on mobile), for desktop rows and mobile cards alike: the lineup (MLB),
// else the analyst's notes on the player and his team. Nothing without either. A note on the player himself turns it gold.
export function NotesPopover({ lineup, teamNotes, playerNotes, teamLabel, playerName, playerImageUrl, sport, onOpenChange, className, dataTour }: {
  lineup?: LineupInfo; teamNotes?: string; playerNotes?: string; teamLabel?: string; playerName?: string; playerImageUrl?: string | null;
  sport: Sport; onOpenChange?: (open: boolean) => void; className?: string; dataTour?: string;
}): React.ReactElement | null {
  if (!lineup && !teamNotes && !playerNotes) return null;
  const playerNote = !lineup && !!playerNotes;
  return (
    <span
      data-tour={dataTour}
      className={cn('inline-flex flex-shrink-0', className)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <RichPopover
        trigger={<LineupTrigger lineup={lineup} sport={sport} playerNote={playerNote} />}
        header={lineup ? <LineupDrawerHeader lineup={lineup} sport={sport} /> : (
          <div className="flex items-center gap-2 min-w-0">
            <NotesIcon sport={sport} className={cn('w-4 h-4 flex-shrink-0', playerNote ? 'text-amber-500' : 'text-muted-foreground')} />
            <span className="text-sm font-semibold text-fg dark:text-text-primary">Notes</span>
          </div>
        )}
        ariaLabel={lineup ? 'Lineup info' : 'Notes'}
        onOpenChange={onOpenChange}
        // A lineup is short; an analyst's note can run to 2000 characters: wider, capped to the viewport and scrolled.
        width={lineup ? 220 : 340}
        maxContentHeight={lineup ? undefined : 'min(24rem, 60vh)'}
        drawerDescription={lineup ? 'Projected or confirmed batting order.' : 'The analyst\'s notes on this player and his team.'}
      >
        <div
          className={cn('px-3 py-2.5 text-sm', !lineup && 'leading-relaxed')}
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <NotesContent lineup={lineup} teamNotes={teamNotes} playerNotes={playerNotes} teamLabel={teamLabel}
            playerName={playerName} playerImageUrl={playerImageUrl} sport={sport} />
        </div>
      </RichPopover>
    </span>
  );
}
